<script setup lang="ts">
import {Badge, DropdownButton, DropdownItem, PhoneInput, TextInput} from "@hantera/design-system";
import useBackend from "@/composition/useBackend";
import {computed, inject, reactive, ref} from "vue";
import localization from "@/localization";
import {renderLanguage, SUPPORTED_LOCALES} from '@/constants/locales'
import {ImageScaler} from '@/utils/image-scaler'
import {legacyModeKey, useDialogs, useLocalization} from '@hantera/portal-app'
import ChangeEmailDialog from './ChangeEmailDialog.vue'
import ChangePasswordDialog from './ChangePasswordDialog.vue'

const { user } = useBackend()
const t = useLocalization('user')
const dialogs = useDialogs()

const legacyMode = inject(legacyModeKey, false);

// Edit mode state
const isEditing = ref(false)
const saving = ref(false)
const uploadingPicture = ref(false)
const resending = ref(false)
const fileInput = ref<HTMLInputElement>()

const editForm = reactive({
  name: '',
  phone: ''
})

// Avatar computed properties
const initials = computed(() => {
  const userName = user.value?.name
  const userEmail = user.value?.email

  if (!userName) {
    return userEmail ? userEmail.substring(0, 2).toUpperCase() : '??'
  }

  const parts = userName.split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return userName.substring(0, 2).toUpperCase()
})

const avatarStyle = computed(() => {
  const picture = user.value?.picture
  if (picture) {
    return {
      backgroundImage: `url(${picture})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }
  }
  return {}
})

// Avatar methods
function triggerFileInput() {
  if (uploadingPicture.value) return
  fileInput.value?.click()
}

async function handleFileSelect(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  try {
    uploadingPicture.value = true
    const dataUrl = await ImageScaler.scaleAndEncode(file)

    await user.updateProfile({ picture: dataUrl })

    input.value = ''
  } catch (error: any) {
    if (error.message.includes('file size')) {
      await dialogs.alert(t('errors.fileTooLarge'), t('errors.uploadError'))
    } else if (error.message.includes('format')) {
      await dialogs.alert(t('errors.invalidFormat'), t('errors.uploadError'))
    } else {
      await dialogs.alert(error.message, t('errors.uploadError'))
    }
  } finally {
    uploadingPicture.value = false
  }
}

async function removePicture() {
  if (uploadingPicture.value) return

  const confirmed = await dialogs.confirm(
    t('dialogs.removePictureConfirm'),
    t('dialogs.confirmRemoval')
  )

  if (!confirmed) return

  try {
    uploadingPicture.value = true
    await user.updateProfile({ picture: '' })
  } finally {
    uploadingPicture.value = false
  }
}

// Edit mode methods
function startEditing() {
  editForm.name = user.value?.name || ''
  editForm.phone = user.value?.phone || ''
  isEditing.value = true
}

function cancelEditing() {
  isEditing.value = false
  editForm.name = ''
  editForm.phone = ''
}

async function saveChanges() {
  // Validate name
  if (!editForm.name?.trim()) {
    await dialogs.alert(t('validation.nameRequired'), t('dialogs.validationError'))
    return
  }

  saving.value = true
  try {
    await user.updateProfile({
      name: editForm.name.trim(),
      phone: editForm.phone?.trim() || undefined
    })

    isEditing.value = false
  } catch (error) {
    // Error handled by service
  } finally {
    saving.value = false
  }
}

// Email change methods
function openEmailChangeDialog() {
  dialogs.openModal(
    t('actions.changeEmailAction'),
    ChangeEmailDialog,
    true,
    {
      currentEmail: user.value?.email || '',
      onEmailChanged: async () => {
        await dialogs.alert(t('messages.verificationSent'), t('dialogs.emailChangeRequested'))
      }
    }
  )
}

// Password change methods
function openPasswordChangeDialog() {
  dialogs.openModal(
    t('actions.changePassword'),
    ChangePasswordDialog,
    true,
    {
      onPasswordChanged: async () => {
        await dialogs.alert(t('messages.passwordChanged'), t('actions.changePassword'))
      }
    }
  )
}

// Resend verification email
async function resendVerificationEmail() {
  const pendingEmail = user.value?.pendingEmail
  if (!pendingEmail) return

  resending.value = true
  try {
    await user.changeEmail(pendingEmail)
    await dialogs.alert(t('messages.verificationResent'), t('dialogs.emailSent'))
  } catch (error) {
    // Error handled by service
  } finally {
    resending.value = false
  }
}

// Time formatting using browser's Intl.RelativeTimeFormat
function formatExpiryTime(expiresAt: Date | undefined): string {
  if (!expiresAt) return ''

  const now = new Date()
  const diffMs = expiresAt.getTime() - now.getTime()

  const rtf = new Intl.RelativeTimeFormat(
    document.documentElement.lang || navigator.language,
    { numeric: 'auto' }
  )

  if (diffMs < 0) {
    return rtf.format(0, 'hour') // "in 0 hours" or localized equivalent
  }

  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays > 0) {
    return rtf.format(diffDays, 'day')
  } else if (diffHours > 0) {
    return rtf.format(diffHours, 'hour')
  } else {
    return rtf.format(diffMinutes, 'minute')
  }
}

// Preferences (existing logic)
const browserLanguage = navigator.languages[0]

const formats: {
  locale: string | null
  name: string
  numberFormatter: Intl.NumberFormat
  dateTimeFormatter: Intl.DateTimeFormat
}[] = SUPPORTED_LOCALES
  .map(locale => ({
    locale,
    name: renderLanguage(locale)!,
    numberFormatter: new Intl.NumberFormat(locale),
    dateTimeFormatter: new Intl.DateTimeFormat(locale),
  }))
formats.sort((a, b) => a.name.localeCompare(b.name))
formats.unshift({
  locale: null,
  name: 'Browser: ' + renderLanguage(browserLanguage),
  numberFormatter: new Intl.NumberFormat(browserLanguage),
  dateTimeFormatter: new Intl.DateTimeFormat(browserLanguage),
})

const formatFilter = ref('')

const filteredFormats = computed(() => formats.filter(format => format.name.toLowerCase().includes(formatFilter.value.toLowerCase())))

async function setLanguage(languageCode: string | null) {
  await user.updateSettings({
    language: languageCode,
  })
}

async function setFormatting(languageCode: string | null) {
  await user.updateSettings({
    formatting: languageCode,
  })
}

const selectedLanguageLabel = computed(() => {
  const language = user.value?.settings?.language
  if (!language) {
    return t('preferences.browser') + ': ' + renderLanguage(browserLanguage)
  }
  return renderLanguage(language)!
})

const selectedFormatLabel = computed(() => {
  let result = ''
  let locale = user.value?.settings?.formatting

  if (!locale) {
    locale = browserLanguage
    result = t('preferences.browser') + ': '
  }

  const numberFormatter = new Intl.NumberFormat(locale)
  const dateTimeFormatter = new Intl.DateTimeFormat(locale)

  result += renderLanguage(locale)!
  result += ' | ' + dateTimeFormatter.format(new Date())
  result += ' | ' + numberFormatter.format(12345.6789)

  return result
})
</script>

<template>
  <div class="user-view p">
    <!-- Avatar Section -->
    <div class="avatar-section">
      <div v-if="!isEditing" class="avatar-container">
        <div
          class="avatar-large"
          :class="{ clickable: !uploadingPicture }"
          :style="avatarStyle"
          @click="triggerFileInput"
          role="button"
          tabindex="0"
          :title="uploadingPicture ? t('avatar.processing') : t('avatar.clickToChange')"
          @keydown.enter="triggerFileInput"
          @keydown.space.prevent="triggerFileInput"
        >
          <span v-if="!user.value?.picture">{{ initials }}</span>
          <div v-if="!uploadingPicture" class="avatar-overlay">
            {{ t('actions.changePicture') }}
          </div>
          <div v-else class="avatar-overlay">
            ⏳
          </div>
        </div>
        <input ref="fileInput" type="file" accept="image/*" @change="handleFileSelect" style="display: none" />
        <button
          v-if="user.value?.picture"
          @click="removePicture"
          class="btn-silent"
          :disabled="uploadingPicture"
        >
          {{ t('actions.removePicture') }}
        </button>
      </div>
      <div v-else class="avatar-large" :style="avatarStyle">
        <span v-if="!user.value?.picture">{{ initials }}</span>
      </div>
    </div>

    <!-- Profile Form -->
    <div class="form">
      <div class="box-content-label">
        {{ t('profile.title') }}
      </div>

      <!-- View Mode -->
      <dl v-if="!isEditing" class="box-content">
        <dt>{{ t('profile.name') }}</dt>
        <dd>{{ user.value?.name }}</dd>

        <dt>{{ t('profile.email') }}</dt>
        <dd class="email-field">
          <span>{{ user.value?.email }}</span>
          <button @click="openEmailChangeDialog" class="btn-silent" v-if="!legacyMode">
            {{ t('actions.changeEmail') }}
          </button>
        </dd>

        <template v-if="user.value?.pendingEmail">
          <dt>{{ t('profile.pendingEmail') }}</dt>
          <dd class="pending-email">
            <div>
              {{ user.value.pendingEmail }}
              <Badge>{{ t('profile.verificationPending') }}</Badge>
              
            </div>
            
            <div>
              <button
                @click="resendVerificationEmail"
                class="btn-primary"
                :disabled="resending"
              >
                {{ resending ? t('actions.resending') : t('actions.resendVerification') }}
              </button>
              
              <span class="expiry-info">{{ t('profile.expiresAt', { time: formatExpiryTime(user.value.pendingEmailExpiresAt) }) }}</span>
            </div>
            
          </dd>
        </template>

        <dt>{{ t('profile.phone') }}</dt>
        <dd>{{ user.value?.phone || '—' }}</dd>
      </dl>

      <!-- Edit Mode -->
      <fieldset v-else class="box-content">
        <div class="field">
          <label for="edit-name">{{ t('profile.name') }}</label>
          <TextInput id="edit-name" v-model="editForm.name" :disabled="saving" />
        </div>
        <div class="field">
          <label for="edit-phone">{{ t('profile.phone') }}</label>
          <PhoneInput v-model="editForm.phone" :disabled="saving" id="edit-phone" />
        </div>
      </fieldset>

      <!-- Action Buttons -->
      <div class="actions" v-if="!legacyMode">
        <template v-if="!isEditing">
          <button @click="openPasswordChangeDialog">
            {{ t('actions.changePassword') }}
          </button>
          <button @click="startEditing">
            {{ t('actions.editProfile') }}
          </button>
        </template>
        <template v-else>
          <button @click="cancelEditing" class="btn-silent" :disabled="saving">
            {{ t('actions.cancel') }}
          </button>
          <button @click="saveChanges" class="btn-primary" :disabled="saving">
            {{ saving ? t('actions.saving') : t('actions.saveChanges') }}
          </button>
        </template>
      </div>
    </div>

    <!-- Preferences (existing) -->
    <div class="form" v-if="!isEditing">
      <div class="box-content-label">
        {{ t('preferences.title') }}
      </div>
      <fieldset class="box-content p">
        <div class="field">
          <label>{{ t('preferences.language') }}</label>
          <DropdownButton :label="selectedLanguageLabel">
            <DropdownItem @click="setLanguage(null)">
              {{ t('preferences.browser') }}: {{ renderLanguage(browserLanguage) }}
            </DropdownItem>
            <DropdownItem
              v-for="language in Object.keys(localization)"
              :key="language"
              @click="setLanguage(language)"
            >
              {{ renderLanguage(language) }}
            </DropdownItem>
          </DropdownButton>
        </div>
        <div class="field">
          <label>{{ t('preferences.formatting') }}</label>
          <DropdownButton :label="selectedFormatLabel">
            <div class="toolbar">
              <TextInput v-model="formatFilter" />
            </div>
            <div class="formats">
              <DropdownItem
                v-for="format in filteredFormats"
                :key="format.locale ?? 'browser'"
                @click="setFormatting(format.locale)"
              >
                <div>{{ format.name }}</div>
                <div>{{ format.dateTimeFormatter.format(new Date()) }}</div>
                <div>{{ format.numberFormatter.format(12345.6789) }}</div>
              </DropdownItem>
            </div>
          </DropdownButton>
        </div>
      </fieldset>
    </div>
  </div>
</template>

<style scoped>
.user-view {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 550px;
  margin: auto;
  padding: 1.5rem;
}

.formats {
  display: grid;
  grid-template-columns: max-content max-content max-content;
  column-gap: 5px;
}

.formats :deep(li),
.formats :deep(li>div) {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
}

/* Avatar Section */
.avatar-section {
  display: flex;
  justify-content: center;
  padding: 1rem 0;
  margin-bottom: 1rem;
}

.avatar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.avatar-large {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: hsl(var(--color-60));
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  font-weight: 600;
  position: relative;
  overflow: hidden;
}

.avatar-large.clickable {
  cursor: pointer;
  transition: opacity 0.2s;
}

.avatar-large.clickable:hover {
  opacity: 0.9;
}

.avatar-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 12px;
  padding: 6px;
  text-align: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.avatar-large.clickable:hover .avatar-overlay {
  opacity: 1;
}

/* Email Field */
.email-field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Pending Email */
.pending-email {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.pending-email div {
  display: flex;
  gap: 10px;
  align-items: center;
}

.expiry-info {
  font-size: 0.875rem;
  color: #666;
}

/* Actions */
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
}

/* Responsive */
@media (max-width: 768px) {
  .avatar-large {
    width: 80px;
    height: 80px;
    font-size: 32px;
  }

  .form {
    max-width: 100%;
  }

  .actions {
    flex-direction: column;
  }

  .actions button {
    width: 100%;
  }
}
</style>
